import { Trash2 } from "lucide-react";
import { type ReactNode, type SyntheticEvent, useState } from "react";
import clsx from "clsx";
import { basisLabel, fromNet, toCents, toNet, toNumber, toStored } from "../../shared/money";
import { AddPicker } from "../../web/components/AddPicker";
import { DraftFields, TextField, useDraftInvalid } from "../../web/components/fields";
import { DraftBar, ErrorNotice, IconButton } from "../../web/components/ui";
import type { SyncOption } from "../../web/components/SyncSelect";
import { WindowPopup } from "../../web/components/WindowPopup";
import { HoldButton, useConfirm } from "../../web/components/Confirm";
import { useMoney } from "../../web/lib/money";
import { Contents } from "./Contents";
import { bundleTotals } from "./bundleTotals";
import { can, itemName, itemTax, usePriceListSaver } from "./data";
import type { ItemProblemId } from "./attention";
import { inUse, isProduct, type MaterialRow, type PackagePart, type PriceListData } from "./meta";
import { splitName } from "./names";
import { needsCheck, samePrice, shownPercent } from "./pricing";
import { ChangeLines } from "./ChangeLines";
import { QuotedAmountField } from "./QuotedAmount";

/*
 * An item built from products (packages.ts, catalogue.ts): its Contents tab (its products, as a bundle's items), on its Price tab
 * its price and markup as they give them, and building one (its More menu).
 */

/** What it's built from: added, removed, moved and counted as a bundle's items are. Its price and cost follow them. */
export function PartsTab({
    data,
    item,
    draft,
    onDraft,
    onOpenItem,
}: Readonly<{
    data: PriceListData;
    item: MaterialRow;
    /** What it's built from being changed (kept while other tabs are open), or null. */
    draft: PackagePart[] | null;
    onDraft: (draft: PackagePart[] | null) => void;
    onOpenItem: (uuid: string) => void;
}>) {
    // Narrowed to the products with one kind of warning (a chip pressed), or null: all.
    const [only, setOnly] = useState<ItemProblemId | null>(null);
    const saved = data.packages[item.uuid] ?? [];
    // Its fields hold what is typed: started again on Cancel and Save.
    const [version, setVersion] = useState(0);
    const done = () => {
        onDraft(null);
        setVersion((v) => v + 1);
    };
    return (
        // A draft: added, taken out, how many and in what order, saved together by its save bar (its cost and price with them), so
        // its fields never say they are saved.
        <DraftFields key={version}>
            <Contents
                data={data}
                of={{
                    kind: "package",
                    uuid: item.uuid,
                    name: item.name,
                    lines: draft ?? saved,
                    canEdit: can(data, "items.pricing") && inUse(item),
                    save: (parts) => {
                        onDraft(sameParts(parts, saved) ? null : parts);
                        return Promise.resolve();
                    },
                    saveBar: draft ? <PartsSave data={data} item={item} draft={draft} onDone={done} /> : undefined,
                }}
                onOpenItem={onOpenItem}
                only={only}
                onOnly={setOnly}
            />
        </DraftFields>
    );
}

const sameParts = (a: readonly PackagePart[], b: readonly PackagePart[]) => a.length === b.length && a.every((p, i) => p.uuid === b[i]?.uuid && p.quantity === b[i]?.quantity);

/**
 * Saving what an item's built from (its Contents tab's draft): what its cost and price become (as its pricing works them out from
 * its products: their prices added up, or its group's markup on their costs; ServiceM8 pricing it, its cost only), Save (held: it
 * goes to ServiceM8) and Cancel on the right.
 */
function PartsSave({ data, item, draft, onDone }: Readonly<{ data: PriceListData; item: MaterialRow; draft: PackagePart[]; onDone: () => void }>) {
    const saver = usePriceListSaver();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    // A quantity typed that isn't one (marked on its row): not saved until it's put right.
    const typedBad = useDraftInvalid();
    const rate = itemTax(data, item);
    const totals = bundleTotals(data, draft);
    const pricing = data.itemPricing[item.uuid];
    // Its cost: its products' added up, in its own terms (with tax or not, as its price).
    const cost = toCents(fromNet(totals.costNet, item.price_includes_taxes, rate.tax));
    // Its price: as its pricing works it out with them (its products' prices added up, or its group's rule on that cost).
    const withParts = pricing?.fromParts ? { ...pricing, basePrice: toStored(totals.priceNet) } : pricing;
    const check = needsCheck({ ...item, cost }, rate, withParts);
    const price = pricing?.pricedInServiceM8 ? null : (check?.target ?? item.price);
    // What changes, as the server sees it (under a cent apart is the same).
    const label = basisLabel(item.price_includes_taxes, rate.rate);
    const costLine = samePrice(item.cost, cost) ? undefined : { from: item.cost, to: cost, label };
    const priceLine = price === null || samePrice(item.price, price) ? undefined : { from: item.price, to: price, fromLabel: label, toLabel: label };
    // None left: nothing to price it (Unlink from Catalogue makes it a plain item instead).
    const empty = draft.length === 0;
    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            await saver.setPackageParts(item.uuid, draft);
            onDone();
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    };
    return (
        <DraftBar
            message={
                empty ? (
                    <span className="block text-danger">Add a product to save: without any, nothing prices it. To price it yourself, Unlink from Catalogue (More).</span>
                ) : (
                    <>
                        <ChangeLines price={priceLine} cost={costLine} />
                        {costLine || priceLine ? null : <span className="block">Its cost and price stay the same.</span>}
                        {price === null ? <span className="block text-xs">Its price is set by ServiceM8&apos;s pricing.</span> : null}
                        {typedBad ? <span className="block text-xs text-danger">Put right what&apos;s marked above to save.</span> : null}
                    </>
                )
            }
            busy={busy}
            error={error}
            actions={
                <>
                    <HoldButton
                        className="btn-primary"
                        label="Save to ServiceM8"
                        disabled={busy || empty || typedBad}
                        title="What it's built from, its cost and price, saved to ServiceM8"
                        onDone={() => void save()}
                    />
                    <button type="button" className="btn-secondary" disabled={busy} onClick={onDone}>
                        Cancel
                    </button>
                </>
            }
        />
    );
}

/**
 * Keeping a built item's own price or cost, not what its products add up to (its price or cost to update, or ServiceM8's strategy's
 * price): only a plain item has its own, so it's made one, asked first (rarely what's wanted: updating it is). True: made plain.
 */
export function useMakePlain() {
    const confirm = useConfirm();
    const saver = usePriceListSaver();
    const fmt = useMoney();
    return async (item: MaterialRow, kept: { what: "price" | "cost"; amount: string | number }) => {
        const yes = await confirm({
            title: `Keep its ${kept.what} of ${fmt.money(kept.amount)}?`,
            message: "Keeping its own means unlinking it from its products in the Catalogue: its price and cost stay as they are now, typed in.",
            detail: "Changes to its products won't show on it any more. Usually it's updated to what its products add up to instead. Its products stay in the Catalogue, so it can be linked to them again.",
            detailTone: "warning",
            confirmLabel: "Unlink it",
            tone: "danger",
        });
        if (yes) await saver.unmakePackage(item.uuid);
        return yes;
    };
}

/**
 * On a built item's Price tab: its price is what its products add up to (changed on its Contents tab, not here), laid out as Manual's
 * and Markup's fields are: its markup on their cost, and its price (how it's taxed picked inside it).
 */
export function PartsPriceCard({ data, item, onCostTab, onParts }: Readonly<{ data: PriceListData; item: MaterialRow; onCostTab: () => void; onParts: () => void }>) {
    const saver = usePriceListSaver();
    const { tax, rate } = itemTax(data, item);
    const shown = (net: number) => fromNet(net, item.price_includes_taxes, tax);
    const totals = bundleTotals(data, data.packages[item.uuid] ?? []);
    // Before tax: what its products give (none yet: its price and cost as they are), and the markup that makes.
    const hasParts = (data.packages[item.uuid] ?? []).length > 0;
    const priceNet = hasParts ? totals.priceNet : toNet(toNumber(item.price), item.price_includes_taxes, tax);
    const costNet = hasParts ? totals.costNet : toNet(toNumber(item.cost), item.price_includes_taxes, tax);
    const markupPct = costNet > 0 ? ((priceNet - costNet) / costNet) * 100 : null;
    return (
        <div className="space-y-4">
            <p className="m-0">
                What its products add up to.{" "}
                <button type="button" className="link-button" onClick={onParts}>
                    See its products
                </button>
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                    label="Markup"
                    value={markupPct === null ? "" : shownPercent(String(markupPct))}
                    placeholder="-"
                    suffix="%"
                    disabled
                    onSave={() => Promise.resolve()}
                    hint={<MarkupHint priceNet={priceNet} costNet={costNet} shown={shown} label={basisLabel(item.price_includes_taxes, rate)} onCostTab={onCostTab} />}
                />
                <QuotedAmountField
                    label="Total price"
                    amount={toCents(shown(priceNet))}
                    quote={{ rateUuid: itemTax(data, item).uuid, includesTax: item.price_includes_taxes }}
                    rates={data.taxRates}
                    disabled={!can(data, "items.pricing")}
                    amountLocked
                    onSave={() => Promise.resolve()}
                    onQuote={(t) => saver.updateMaterial(item.uuid, { tax_rate_uuid: t.rateUuid, price_includes_taxes: t.includesTax })}
                />
            </div>
        </div>
    );
}

/**
 * Building an item from products (its More menu): it stays the item jobs and the accounting package know (its name, number and
 * history), its price and cost what its products add up to from now on. What it is now can be its first product (a must with
 * supplier prices or pricing of its own: only products have them), a new one in the Catalogue taking its price, cost, pricing and
 * supplier prices, so its price doesn't move; more can be added here (labour...), each from the Catalogue (packages.ts makePackage).
 */
export function BuildPopup({ data, item, onClose, onMade }: Readonly<{ data: PriceListData; item: MaterialRow; onClose: () => void; onMade: () => void }>) {
    const saver = usePriceListSaver();
    const fmt = useMoney();
    const prices = data.supplierPrices.filter((p) => p.materialUuid === item.uuid).length;
    // Priced here from before (a rule or group): that goes to the product too.
    const priced = data.itemPricing[item.uuid] !== undefined;
    const mustKeep = prices > 0 || priced;
    const [withProduct, setWithProduct] = useState(true);
    const [extra, setExtra] = useState<PackagePart[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const byUuid = new Map(data.materials.map((m) => [m.uuid, m]));
    const productName = splitName(item.name, data.nameTags).base.trim() || item.name;
    const own = item.item_number.trim();
    const ready = withProduct || extra.length > 0;
    // What its new product takes, beyond its price and cost: "pricing and 2 supplier prices".
    const takes = [priced ? "pricing" : "", prices ? `${prices} supplier ${prices === 1 ? "price" : "prices"}` : ""].filter(Boolean);
    // What it'll sell for, in its terms: what it is now (its new product's) and what's added.
    const { tax } = itemTax(data, item);
    const added = bundleTotals(data, extra).priceNet;
    const priceAfter = fromNet((withProduct ? toNet(toNumber(item.price), item.price_includes_taxes, tax) : 0) + added, item.price_includes_taxes, tax);
    const choices: SyncOption[] = data.materials
        .filter((m) => inUse(m) && isProduct(m) && !extra.some((p) => p.uuid === m.uuid))
        .map((m) => ({ value: m.uuid, label: itemName(data, m), detail: m.item_number || undefined }));
    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (!ready) return;
        setBusy(true);
        setError(null);
        saver
            .makePackage({ itemUuid: item.uuid, ...(withProduct ? { product: { name: productName, item_number: own } } : {}) }, extra)
            .then(onMade)
            .catch((err: Error) => {
                setError(err);
                setBusy(false);
            });
    };
    return (
        <WindowPopup top title="Link to Catalogue" onClose={onClose}>
            <form className="flex flex-col gap-3" onSubmit={submit}>
                <p className="m-0 text-muted">Its price and cost come from products in the Catalogue. It stays the item jobs and your accounting know.</p>
                {/* Its products, one list: the search to add more, then what it is now (a new product, as it is), then the others. */}
                <div className="space-y-2 rounded-lg border border-line bg-surface-2 p-3">
                    <span className="field-label block">Products</span>
                    {/* Adding products: a search at the top of the list, as a bundle's Contents; its results push the list down (they stay in the popup). */}
                    <AddPicker
                        pushDown
                        placeholder="Add a product (labour, parts): search by name or item number"
                        options={choices}
                        onAdd={async (uuid) => setExtra((list) => [...list, { uuid, quantity: 1 }])}
                        onUndo={async (uuid) => setExtra((list) => list.filter((x) => x.uuid !== uuid))}
                    />
                    {withProduct ? (
                        <div className="flex items-start gap-2">
                            <span className="w-14 shrink-0 pt-1.5 text-center tabular-nums">1</span>
                            <span className="min-w-0 flex-1 pt-1.5">
                                <span className="flex min-w-0 items-center gap-2">
                                    <span className="truncate">
                                        {productName}
                                        {own ? <span className="text-muted"> · {own}</span> : null}
                                    </span>
                                    <span className="shrink-0 rounded-full border border-line bg-surface px-1.5 text-xs font-medium text-brand-ink">New</span>
                                </span>
                                <span className="block text-(length:--label-text) text-muted">
                                    What it is now, as a product: takes its price and cost{takes.length ? `, ${takes.join(" and ")}` : ""}
                                </span>
                            </span>
                            {/* With supplier prices or pricing, it's a must (only products have them). */}
                            {mustKeep ? (
                                <span className="w-8 shrink-0" />
                            ) : (
                                <IconButton label="Don't make a product of it" onClick={() => setWithProduct(false)}>
                                    <Trash2 className="size-4" />
                                </IconButton>
                            )}
                        </div>
                    ) : (
                        <button type="button" className="link-button text-sm" onClick={() => setWithProduct(true)}>
                            Make a product of what it is now
                        </button>
                    )}
                    {extra.map((p) => {
                        const m = byUuid.get(p.uuid);
                        return (
                            <div key={p.uuid} className="flex items-center gap-2">
                                <PartQuantity
                                    quantity={p.quantity}
                                    onChange={(quantity) => setExtra((list) => list.map((x) => (x.uuid === p.uuid ? { ...x, quantity } : x)))}
                                />
                                <span className="min-w-0 flex-1 truncate">
                                    {m ? itemName(data, m) : "A product"}
                                    {m?.item_number ? <span className="text-muted"> · {m.item_number}</span> : null}
                                </span>
                                <IconButton label="Remove" onClick={() => setExtra((list) => list.filter((x) => x.uuid !== p.uuid))}>
                                    <Trash2 className="size-4" />
                                </IconButton>
                            </div>
                        );
                    })}
                </div>
                {ready ? (
                    <p className="m-0 text-(length:--label-text) text-muted">
                        Its price: <span className="font-medium text-ink tabular-nums">{fmt.money(priceAfter)}</span>
                        {Math.abs(priceAfter - toNumber(item.price)) >= 0.005 ? ` (now ${fmt.money(item.price)})` : ", as now"}
                    </p>
                ) : null}
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !ready}>
                        {busy ? "Linking..." : "Link it"}
                    </button>
                </div>
            </form>
        </WindowPopup>
    );
}

/**
 * How many of a part, typed (an hourly labour rate's in hours). Kept as typed while it's typed ("0." on the way to 0.5): only an
 * amount above 0 is passed on.
 */
function PartQuantity(props: Readonly<{ quantity: number; onChange: (quantity: number) => void }>) {
    const [text, setText] = useState(() => String(props.quantity));
    const bad = !(Number(text) > 0);
    return (
        <input
            className={clsx("input w-14 shrink-0 text-center", bad && "is-warning")}
            aria-label="Quantity"
            inputMode="decimal"
            value={text}
            onChange={(e) => {
                setText(e.target.value);
                const n = Number(e.target.value);
                if (n > 0) props.onChange(n);
            }}
            onBlur={() => setText(String(props.quantity))}
        />
    );
}

/** Under a linked item's markup: what it adds on its cost ("+$30.67 inc GST on its $29.33 cost", the cost its Costing tab), or nothing without a cost. */
export function MarkupHint(
    props: Readonly<{ priceNet: number; costNet: number; shown: (net: number) => number; label: string; onCostTab: () => void; /** Said with no cost (a labour time's: its rate has none). */ noCost?: ReactNode }>,
) {
    const fmt = useMoney();
    const { priceNet, costNet, shown } = props;
    if (costNet <= 0) return props.noCost ?? null;
    return (
        <>
            <span className={clsx(priceNet < costNet && "text-danger")}>
                {priceNet < costNet ? "-" : "+"}
                {fmt.money(Math.abs(shown(priceNet - costNet)))} {props.label}
            </span>{" "}
            on its{" "}
            <button type="button" className="cursor-pointer whitespace-nowrap text-brand-ink hover:underline" title="Its cost (the Costing tab)" onClick={props.onCostTab}>
                {fmt.money(shown(costNet))} cost
            </button>
        </>
    );
}
